import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ThemeContext = createContext(null);

// Default theme colors
const defaultTheme = {
  id: 'default',
  name: 'Default Blue',
  colors: {
    primary: 'var(--theme-primary)',
    primary_hover: 'var(--theme-primary-hover)',
    primary_light: 'rgba(41, 173, 211, 0.1)',
    secondary: 'var(--theme-secondary)',
    accent: 'var(--theme-primary)',
    header_bg: 'var(--theme-primary)',
    button_bg: 'var(--theme-primary)',
    button_hover: 'var(--theme-primary-hover)',
    link_color: 'var(--theme-primary)',
    border_focus: 'var(--theme-primary)',
    gradient_start: 'var(--theme-primary)',
    gradient_end: 'var(--theme-secondary)'
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    const origin = window.location.origin;
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8001';
    }
    return origin;
  };

  // Fetch active theme on mount
  useEffect(() => {
    const fetchActiveTheme = async () => {
      try {
        const backendUrl = getBackendURL();
        const response = await axios.get(`${backendUrl}/api/website-themes/active`);
        if (response.data.success && response.data.theme) {
          setTheme(response.data.theme);
        }
      } catch (error) {
        console.error('Error fetching active theme:', error);
        // Use default theme on error
      } finally {
        setLoading(false);
      }
    };

    fetchActiveTheme();
  }, []);

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    if (theme?.colors) {
      const root = document.documentElement;
      
      // Set CSS custom properties
      root.style.setProperty('--theme-primary', theme.colors.primary);
      root.style.setProperty('--theme-primary-hover', theme.colors.primary_hover);
      root.style.setProperty('--theme-primary-light', theme.colors.primary_light);
      root.style.setProperty('--theme-secondary', theme.colors.secondary);
      root.style.setProperty('--theme-accent', theme.colors.accent);
      root.style.setProperty('--theme-ui-blue', theme.colors.ui_blue || 'rgb(59, 130, 246)');
      root.style.setProperty('--theme-ui-blue-light', theme.colors.ui_blue_light || 'rgb(219, 234, 254)');
      root.style.setProperty('--theme-header-bg', theme.colors.header_bg);
      root.style.setProperty('--theme-button-bg', theme.colors.button_bg);
      root.style.setProperty('--theme-button-hover', theme.colors.button_hover);
      root.style.setProperty('--theme-link-color', theme.colors.link_color);
      root.style.setProperty('--theme-border-focus', theme.colors.border_focus);
      root.style.setProperty('--theme-gradient-start', theme.colors.gradient_start);
      root.style.setProperty('--theme-gradient-end', theme.colors.gradient_end);
    }
  }, [theme]);

  // Function to refresh theme (call this after theme changes in admin)
  const refreshTheme = async () => {
    try {
      const backendUrl = getBackendURL();
      const response = await axios.get(`${backendUrl}/api/website-themes/active`);
      if (response.data.success && response.data.theme) {
        setTheme(response.data.theme);
      }
    } catch (error) {
      console.error('Error refreshing theme:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, refreshTheme, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper function to get theme color (for inline styles)
export const getThemeColor = (colorKey) => {
  return `var(--theme-${colorKey.replace(/_/g, '-')})`;
};

export default ThemeProvider;
